import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Link, Stack, Typography } from '@mui/material';
import { PhxBadge } from '_lex/_atoms/PhxBadge';

import { AppContext } from '../../../../context';
import { translate as t } from 'hooks/useTranslations';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import { useField } from '../_data_sources/useField';
import useUrlParams from 'hooks/useUrlParams';
import Overview from './Overview';
import ChangeLog from './ChangeLog';
import EditField from './EditField';

export type FieldTab = {
  label: React.ReactNode;
  key: string;
  component: React.ReactNode;
  count?: number;
};

export type FieldUrlParams = {
  tab: string;
};

const defaultParams: FieldUrlParams = {
  tab: 'overview',
};

export default function Details() {
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const { id } = useParams();
  const fieldQuery = useField(Number(id), true);
  const [params, setParams] = useUrlParams(defaultParams);

  const tabs: { [key: string]: FieldTab } = React.useMemo(() => {
    const baseTabs: { [key: string]: FieldTab } = {
      overview: {
        label: t('performanceReporting.fieldBank.details.tabs.overview'),
        key: 'overview',
        component: <Overview />,
      },
    };

    baseTabs.changelog = {
      label: t('performanceReporting.fieldBank.details.tabs.changelog'),
      key: 'changelog',
      component: <ChangeLog />,
    };

    return baseTabs;
  }, [fieldQuery.data?.status, t]);

  const updateParams = React.useCallback(
    (updatedParams: FieldUrlParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  // Redirect to overview tab if user is on changelog tab but field doesn't support it
  React.useEffect(() => {
    if (params.tab === 'changelog' && !tabs.changelog) {
      updateParams({ tab: 'overview' });
    }
  }, [params.tab, tabs.changelog, updateParams]);

  const headerTitle = fieldQuery.data?.name || '';

  const breadcrumbItems = [
    <Link
      key='b1'
      href='..'
    >
      {t('performanceReporting.fieldBank.list.title')}
    </Link>,
    <Typography key='b4'>{fieldQuery.data?.name || ''}</Typography>,
  ];

  const editButton =
    currentOrganizationId && hasPermission('manageFieldBank', currentOrganizationId) ? (
      <Button
        variant='contained'
        size='small'
        href={`../edit/${fieldQuery.data?.id}`}
      >
        {t('performanceReporting.fieldBank.details.actions.edit')}
      </Button>
    ) : null;

  return (
    <CommonLayout
      headerProps={{
        title: headerTitle,
        titleBadge:
          fieldQuery.data?.status !== undefined ? (
            <PhxBadge
              color={
                fieldQuery.data?.status.toLowerCase() === 'active'
                  ? 'success'
                  : fieldQuery.data?.status.toLowerCase() === 'draft'
                    ? 'neutral'
                    : 'error'
              }
              size='sm'
            >
              {fieldQuery.data?.status}
            </PhxBadge>
          ) : undefined,
        breadcrumbItems,
        actions: null, //editButton,
      }}
      tabs={{
        items: Object.entries(tabs).map(([key, tab]) => ({
          label: tab.label,
          value: key,
          count: tab.count,
        })),
        value: params.tab,
        onChange: (event: React.SyntheticEvent, newValue: string | number) =>
          updateParams({ tab: newValue.toString() }),
      }}
    >
      {tabs[params.tab]?.component}
    </CommonLayout>
  );
}

export { EditField };
